<script setup lang="ts">
import type {Component} from 'vue'
import {computed} from 'vue'
import {PropertyValueType} from '@toolify/server/src/services/DatabasePropertyService/types/PropertyValueType'
import {
  DatabasePropertyValueEditStrategyByTypeMap,
} from '@toolify/client/src/modules/databases/maps/DatabasePropertyValueEditStrategyByTypeMap'
import {
  DatabasePropertyValueStrategyByTypeMap,
} from '@toolify/client/src/modules/databases/maps/DatabasePropertyValueReadStrategyByTypeMap'
import MaskedItemValueStrategy
  from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/components/MaskedItemValueStrategy/MaskedItemValueStrategy.vue'
import {IPropertyValueStrategyPayload} from '@toolify/client/src/modules/tableItems/types/IPropertyValueStrategyPayload'
import {PropertyStrategyThemeType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyThemeType'

interface Props {
  valueType: PropertyValueType | null
  listeners?: unknown
  valuePayload: unknown
  relativeToDate?: Date
  cellPayload?: unknown
  isFocused: boolean
  isInEditMode: boolean
  isCellHidden?: boolean
  isInPreviewMode?: boolean
  isMasked?: boolean
  isRevealable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  listeners: null,
  relativeToDate: null,
  cellPayload: null,
  isCellHidden: false,
  isMasked: false,
  isRevealable: true,
  valueType: null,
})

const isMasked = computed(() => {
  if (props.isMasked) {
    return true
  }
  const payload = props.valuePayload as Partial<IPropertyValueStrategyPayload> | null
  return !!payload?.property?.isMasked && !!payload.itemId && payload.stylesType !== PropertyStrategyThemeType.Form
})

const strategyComponent = computed<Component>(() => {
  if (isMasked.value) {
    return MaskedItemValueStrategy
  }
  if (DatabasePropertyValueEditStrategyByTypeMap.has(props.valueType) && (props.isInEditMode || props.isInPreviewMode)) {
    return DatabasePropertyValueEditStrategyByTypeMap.get(props.valueType)
  }
  return DatabasePropertyValueStrategyByTypeMap.get(props.valueType)
})

const optionalStrategyProps = computed(() => ({
  ...(props.isCellHidden ? {isCellHidden: true} : {}),
  ...(props.isInPreviewMode ? {isInPreviewMode: true} : {}),
  ...(isMasked.value ? {isRevealable: props.isRevealable} : {}),
}))
</script>

<template>
  <Component
    :is="strategyComponent"
    :payload="props.valuePayload"
    :relativeToDate="props.relativeToDate"
    :cellPayload="props.cellPayload || {}"
    :isFocused="props.isFocused"
    :isInEditMode="props.isInEditMode"
    v-bind="optionalStrategyProps"
    v-on="props.listeners || {}"
  />
</template>
